import type { BootLaunchPayload } from "./BootLaunchPayload";
import type { BetLockReason } from "../bet/BetSystemTypes";
import type { GameAudioOverrides } from "../audio/banks/types";
import type { FeatureHudPort } from "../feature-hud/FeatureHudPort";
import type { AudioService } from "../audio/ports/AudioService";
import { InMemoryCurrencyRegistry } from "../money/CurrencyRegistry";
import { createMoneyContext } from "../money/createMoneyContext";
import { createBetSystem } from "../bet/createBetSystem";
import { FeatureModeStore } from "../feature-mode/FeatureModeStore";
import { AutoSpinStore } from "../autospin/AutoSpinStore";
import { SpinPhaseStore } from "../spin/SpinPhaseStore";
import { InteractionBlockStore } from "../interaction-block/InteractionBlockStore";
import { BalanceStore } from "../balance/BalanceStore";
import { CurrentWinStore } from "../win/current-win/CurrentWinStore";
import { CurrentWinSpinBoundary } from "../win/current-win/CurrentWinSpinBoundary";
import { FeatureStateStore } from "../feature-state/FeatureStateStore";
import { CanvasOverlayStore } from "../canvas-overlay/CanvasOverlayStore";
import { FeatureHudPresenter } from "../feature-hud/FeatureHudPresenter";
import { createAudioService } from "../audio/createAudioService";
import { AudioSettingsStore } from "../audio/AudioSettingsStore";
import { AudioSettingsService } from "../audio/AudioSettingsService";
import { createHapticsService } from "../haptics/HapticsService";
import { HapticsSettingsStore } from "../haptics/HapticsSettingsStore";
import { RecordingAudioService } from "../audio/debug/RecordingAudioService";
import { RecordingHapticsService } from "../haptics/debug/RecordingHapticsService";
import { mergeAudioMapping } from "../audio/default-pack/defaultMapping";
import type { CoreServices } from "./CoreServices";
import { FixedPresentationClock, PresentationBarrier, PresentationClock, PresentationTaskRunner } from "@slot/presentation";
import type { Log, PresentationTimerDriver, SpeedModeScales } from "@slot/presentation";

/** A HUD that renders nothing — headless checks. */
export const NULL_FEATURE_HUD_PANEL: FeatureHudPort = {
  show(): void {},
  hide(): void {},
  setAccumulatedWinText(): void {},
  setSpinsLeftText(): void {},
};

/** Dev-mode decorators over the audio + haptics services; the debug hook reads their logs. */
export interface DevRecorders {
  audio: RecordingAudioService;
  haptics: RecordingHapticsService;
}

/**
 * Builds the `CoreServices` record
 * as a function: money, bet (locked for boot), state stores, presentation
 * system (barrier / clock / runner on the injected timer), feature HUD
 * presenter, audio + haptics services with their settings stores. In dev mode both services
 * are wrapped in recording decorators (returned as `devRecorders`) for the debug hook.
 */
export function createCoreServices(input: {
  payload: BootLaunchPayload;
  log: Log;
  presentationTimerDriver: PresentationTimerDriver;
  /** The game's turbo speeds (`GameDefinition.speedModeScales`); absent = the platform's. */
  speedModeScales?: SpeedModeScales;
  featureHud?: FeatureHudPort;
  gameAudioOverrides: GameAudioOverrides | null;
  denominationFactor: number;
  betLockReason?: BetLockReason;
  /** The balance store's opening value; defaults to the settings snapshot (replay mode passes "0"). */
  initialBalance?: string;
  /** The money's currency; defaults to the settings snapshot's (replay mode passes the round's, decision 81). */
  currencyCode?: string;
  /** Mute at start; `startGame` never passes it (plan §1.3). */
  muteAudioOnStart?: boolean;
}): { services: CoreServices; audioService: AudioService; featureHudPresenter: FeatureHudPresenter; devRecorders: DevRecorders | null } {
  const { payload, log } = input;

  const moneyContext = createMoneyContext({
    currencyCode: input.currencyCode ?? payload.initialSettings.accountBalanceSnapshot.currencyCode,
    denominationFactor: input.denominationFactor,
    registry: new InMemoryCurrencyRegistry(payload.currencyCatalog),
  });

  const betSystem = createBetSystem({
    config: {
      betStepsMinor: payload.initialSettings.betStepsMinor,
      initialBetMinor: payload.initialSettings.initialBetMinor,
      // A title whose bet covers lines (decision 56): the ladder is a line's bet.
      lineOptions: payload.initialSettings.betLineOptions,
      initialLines: payload.initialSettings.initialBetLines,
    },
    moneyContext,
  });
  betSystem.lock(input.betLockReason ?? "boot_loading");

  const featureModeStore = new FeatureModeStore();
  const autoSpinStore = new AutoSpinStore();
  const spinPhaseStore = new SpinPhaseStore();
  const interactionBlockStore = new InteractionBlockStore();
  const balanceStore = new BalanceStore(input.initialBalance ?? payload.initialSettings.accountBalanceSnapshot.balanceAmount);
  const currentWinStore = new CurrentWinStore();
  const currentWinSpinBoundary = new CurrentWinSpinBoundary({ store: currentWinStore });

  const featureStateStore = new FeatureStateStore();
  const canvasOverlayStore = new CanvasOverlayStore();
  const localization = payload.localization;
  const presentationBarrier = new PresentationBarrier();
  const presentationClock = new PresentationClock(input.speedModeScales);
  const chromeTaskRunner = new PresentationTaskRunner({
    clock: new FixedPresentationClock(1),
    timer: input.presentationTimerDriver,
    log,
  });
  const presentationTaskRunner = new PresentationTaskRunner({
    clock: presentationClock,
    log,
    slamFinishSeconds: 0.1,
    timer: input.presentationTimerDriver,
  });

  const featureHudPresenter = new FeatureHudPresenter({
    panel: input.featureHud ?? NULL_FEATURE_HUD_PANEL,
    log,
    featureStateStore,
    interactionBlockStore,
    moneyContext,
    runner: presentationTaskRunner,
  });
  featureHudPresenter.start();

  const engineAudioService = createAudioService({
    gameOverrides: input.gameAudioOverrides,
    dev: payload.devModeEnabled,
    devLog: (m) => log(m),
    getActionIsSkippable: () => false,
    resolveBundleAudioUrl: payload.assets.resolveAudioUrl,
  });
  const devRecorders: DevRecorders | null = payload.devModeEnabled
    ? {
        audio: new RecordingAudioService(engineAudioService, mergeAudioMapping(input.gameAudioOverrides?.mapping)),
        haptics: new RecordingHapticsService(createHapticsService({ log })),
      }
    : null;
  const audioService: AudioService = devRecorders?.audio ?? engineAudioService;
  const audioSettingsStore = new AudioSettingsStore();
  if (input.muteAudioOnStart) {
    audioSettingsStore.setMasterVolume(0);
    audioSettingsStore.setMusicEnabled(false);
  }
  const audioSettingsService = new AudioSettingsService(audioSettingsStore, audioService);
  audioSettingsService.start();

  // The store owns the haptics preference for the session; the service follows it, from the first value on.
  const hapticsService = devRecorders?.haptics ?? createHapticsService({ log });
  const hapticsSettingsStore = new HapticsSettingsStore();
  hapticsService.setEnabled(hapticsSettingsStore.getSnapshot().hapticsEnabled);
  hapticsSettingsStore.subscribe(() => {
    hapticsService.setEnabled(hapticsSettingsStore.getSnapshot().hapticsEnabled);
  });

  const services: CoreServices = {
    canvasOverlayStore,
    log,
    moneyContext,
    initialSettings: payload.initialSettings,
    launchContext: payload.launchContext,
    betSystem,
    balanceStore,
    currentWinStore,
    currentWinSpinBoundary,
    featureHudPresenter,
    presentationBarrier,
    presentationTaskRunner,
    chromeTaskRunner,
    presentationClock,
    audioService,
    localization,
    audioSettingsStore,
    audioSettingsService,
    hapticsService,
    hapticsSettingsStore,
    featureModeStore,
    featureStateStore,
    autoSpinStore,
    spinPhaseStore,
    interactionBlockStore,
    gameVersion: payload.gameVersion,
  };

  return { services, audioService, featureHudPresenter, devRecorders };
}
